import type {
  AdditionalField,
  CMSFormDataResponse,
  FormField,
  SubmitMethod,
  SubmitTo,
} from '@/services/cms/getCMSForm/schema';
import { match } from 'ts-pattern';
import { z } from 'zod';
import {
  generateSelectValidationRules,
  generateTextAreaValidationRules,
  generateTextValidationRules,
} from './validation-rules';
import { FORM_WHITELIST_KEYS } from '@/constants/form.constants';
import type { AnyFieldApi, AnyFieldMeta } from '@tanstack/react-form';

export interface DynamicForm {
  formFields: FormField[];
  meta: {
    submitButton: SubmitTo;
    method: SubmitMethod;
    addititionalField: AdditionalField;
  };
}

type ErrorResponse = {
  errors: {
    [key: string]: string[];
  };
};

export const hasFieldErrors = (field: AnyFieldApi) => {
  return field.state.meta.isTouched && field.state.meta.errors.length > 0;
};

export const hasFieldErrorsV2 = (meta: AnyFieldMeta) => {
  return meta.isTouched && meta.errors.length > 0;
};

export const parseFormError = (response: ErrorResponse) =>
  Object.keys(response.errors).flatMap((key) => response.errors[key]);

export const parseDynamicForm = (form: CMSFormDataResponse): DynamicForm => {
  // NOTE: we are filtering out submit method, submit to, additional_field because we are sure that they will always be part of CMS Form Response.
  // submit method = POST | PUT | PATCH | DELETE
  // submit to = submit button + url
  // additional_field = enquiringAbout: "exetelComplaints"
  //
  const fieldArray = Object.keys(form.fields)
    .filter((key) => !FORM_WHITELIST_KEYS.includes(key))
    .map((key) => form.fields[key]);

  return {
    formFields: fieldArray,
    meta: {
      submitButton: form.fields.submit_to,
      method: form.fields.submit_method,
      addititionalField: form.fields.additional_field,
    },
  };
};

export const parseAdditionalFormField = (s: string) => {
  try {
    return JSON.parse(s);
  } catch (e) {
    console.error('Not a valid JSON', e);
    return null;
  }
};

export const generateValidationRules = (form: FormField) =>
  match(form)
    .with({ type: 'text' } as const, (formData) =>
      generateTextValidationRules(formData)
    )
    .with({ type: 'textarea' } as const, (formData) =>
      generateTextAreaValidationRules(formData)
    )
    .with({ type: 'select' } as const, (formData) =>
      generateSelectValidationRules(formData)
    )
    .otherwise(() => z.string());

export const generateFormDefaultValues = (formFields: FormField[]) =>
  formFields.reduce(
    (acc, field) => {
      acc[field.handle] = '';
      return acc;
    },
    {} as Record<string, string> //TODO: Improve this, we are loosing type safety here, but maybe it's fine? maybe create a custom type?
    // Example: Record<string, string | number | boolean> to support input texts, checkboxes, radio buttons, input number, etc.
  );
